import { Instructions } from "@amzn/itsvideo-recorder/lib/VideoRecorder";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Radio } from "@amzn/stencil-react-components/radio";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import { useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";
import VideoGuidance from "@/components/Commons/VideoGuidance";
import VideoRecorderCustom from "@/components/Commons/VideoRecorderCustom";
import {
  VideoGuidanceName,
  VIDEOS_PLAYBACK_CONFIGS,
} from "@/components/constants";
import DosDonts from "@/components/IdentityCheck/DosDonts";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import { RecordIdStepProps } from "./FullIdVerification";
import { VideoOverlay } from "./ManualVerificationOverlays";

/**
 * View for uploading NH's secondary ID.
 */
export default function SecondaryId({
  clearSelectedStep,
  fullIdVerificationElementRef,
}: RecordIdStepProps): JSX.Element {
  const intl = useIntl();

  const { dispatch, state } = useContext(AppContext);
  const [isGovernmentIssued, setIsGovernmentIssued] = useState<
    boolean | undefined
  >(undefined);

  const instructions: Instructions[] = [
    {
      text: intl.formatMessage({
        id: "identityCheck-secondaryId-video-instruction",
      }),
      bannerBackGroundColor: "primary80",
      displayTimestampMs: 0,
    },
    {
      text: intl.formatMessage({
        id: "identityCheck-secondaryId-video-instruction2",
      }),
      bannerBackGroundColor: "pink70",
      displayTimestampMs: 5_000,
    },
  ];

  const handleGovIssuedOnChange = (govIssued: boolean) => {
    dispatch({
      type: ActionType.SET_SECONDARY_ID_GOV_ISSUED,
      secondaryIdGovIssued: govIssued,
    });
    setIsGovernmentIssued(govIssued);
    trackStructEvent({
      category: Day1EventCategory.IDCheckFullSec,
      action: govIssued
        ? Day1EventAction.SelectGovernmentIssuedId
        : Day1EventAction.SelectNonGovernmentIssuedId,
    });
  };

  return (
    <Col gridGap="S300">
      <Col gridGap="S400" dataTestId="secondaryIdIntroCol">
        <StepBodyStyledText>
          <Text fontWeight="medium">
            <FormattedMessage id="identityCheck-full-steps-recordSecondaryId" />
          </Text>
        </StepBodyStyledText>
        <StepBodyStyledText>
          <Text fontWeight="regular">
            <FormattedMessage id="identityCheck-secondaryId-instruction" />
          </Text>
        </StepBodyStyledText>
      </Col>
      <StepBodyStyledText>
        <Text fontWeight="medium">
          <FormattedMessage id="identityCheck-full-steps-secondaryIdGovIssued" />
        </Text>
      </StepBodyStyledText>
      <StepBodyStyledText>
        <Text fontWeight="regular">
          <FormattedMessage id="identityCheck-secondaryIdGovIssued-instruction" />
        </Text>
      </StepBodyStyledText>
      <Col padding="S300" gridGap="S300">
        <Row gridGap="S200" alignItems="center">
          <Radio
            checked={isGovernmentIssued === true}
            name="radio-government"
            onChange={() => handleGovIssuedOnChange(true)}
          />
          <Label htmlFor="radio-government">
            <FormattedMessage id="identityCheck-secondaryID-governmentID-radio1" />
          </Label>
        </Row>
        <Row gridGap="S200" alignItems="center">
          <Radio
            checked={isGovernmentIssued === false}
            name="radio-non-government"
            onChange={() => handleGovIssuedOnChange(false)}
          />
          <Label htmlFor="radio-non-government">
            <FormattedMessage id="identityCheck-secondaryID-governmentID-radio2" />
          </Label>
        </Row>
      </Col>
      <DosDonts isIdPresent />
      <VideoGuidance
        videoFileConfig={
          VIDEOS_PLAYBACK_CONFIGS[VideoGuidanceName.SECONDARY_ID_DESKTOP]
        }
        eventCategory={Day1EventCategory.IDCheckFullSec}
        titleDataTestId="secondaryIdGuidanceTitle"
        overlayTextI18nId="videoGuidance-example"
      />
      <VideoRecorderCustom
        clearSelectedStep={clearSelectedStep}
        fullIdVerificationElementRef={fullIdVerificationElementRef}
        recordCompletedI18nId="identityCheck-secondaryId-completed"
        instructions={instructions}
        documentType="SECONDARY_ID_RECORDING"
        eventCategory={Day1EventCategory.IDCheckFullSec}
        refPage="ezo-identity-check-manual-full-sec"
        overlays={VideoOverlay}
        enableDocumentCapture
      />
    </Col>
  );
}
